/**
 * Shared HTTP transport for the Alveary API.
 *
 * Owns base-URL normalization, the singleton Axios instance, MSAL bearer
 * attachment, how long a request waits, and FastAPI error normalization —
 * including the closed reading of *how* a request failed, which is decided
 * here once because only this boundary can see whether a response existed.
 * Domain modules (./projects.ts, ./tokens.ts, …) build endpoints on top of
 * this client; UI code consumes them through the `api` facade in ./index.ts
 * and should not import this module directly.
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import type { ApiError, ApiFailure } from '@/types/api'
import {
  extractApiErrorDetailBody,
  extractApiErrorDomain,
  extractApiErrorExtra,
  normalizeApiErrorDetail,
} from '@/utils/errors'

/**
 * The default wait for a route that states no budget of its own. It is a
 * client-side policy over ordinary reads and writes, not a mirror of any
 * server bound — the four routes that dial a provider outlive it and say so
 * through `GET /api/v1/model-capabilities/request-budgets/`.
 */
const DEFAULT_TIMEOUT_MS = 30_000

/**
 * What the console adds to a server-stated budget before giving up.
 *
 * A fact about this client's own transport — TLS, the proxy hop, a body it
 * still has to read — and deliberately the one number in this pairing the
 * console owns: the verb's bound belongs to the server and is asked for, the
 * margin over it does not exist there to be asked about.
 */
export const TRANSPORT_MARGIN_MS = 10_000

/**
 * Per-request config for a call whose wait the server stated.
 *
 * The margin is applied here rather than at each call site so no verb can be
 * given a deadline shorter than the bound it was told about.
 */
export const serverBudgetTimeout = (
  budgetSeconds: number
): { timeout: number } => ({
  timeout: Math.round(budgetSeconds * 1000) + TRANSPORT_MARGIN_MS,
})

/** What a FastAPI error body may carry, as this boundary reads it. */
interface ApiErrorBody {
  message?: string
  error?: string
  detail?: unknown
}

/**
 * Which way a request failed when no response came back.
 *
 * Axios raises ECONNABORTED when its own timeout fires and ETIMEDOUT for a
 * socket-level one; a withdrawn request (an AbortSignal, or the auth-redirect
 * path above) is a cancellation and not a fault at all.
 */
const transportFailure = (error: AxiosError): Exclude<ApiFailure, 'response'> => {
  if (axios.isCancel(error)) return 'canceled'
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return 'timeout'
  return 'network'
}

const normalizeApiUrl = (url: string): string => {
  if (!url) {
    console.warn('No API URL configured, using default localhost')
    return 'http://localhost:8000'
  }

  // Remove trailing slash for consistency
  url = url.replace(/\/$/, '')

  // Validate URL format
  try {
    new URL(url)
  } catch (error) {
    console.error(`Invalid API URL: ${url}`, error)
    return 'http://localhost:8000'
  }

  return url
}

/**
 * Parses a `Retry-After` header into seconds from now. The header may
 * legally arrive as either an integer delay-seconds or an HTTP-date, and a
 * caller showing a countdown needs one seconds value regardless of which
 * form showed up. slowapi's `headers_enabled` emits the integer form; the
 * HTTP-date branch exists for any other origin (a proxy, a future limiter)
 * that prefers it. Undefined when the header is absent or unparseable.
 *
 * Exported because ./askStream.ts reads the same header off a `fetch`
 * Response: two parsers would let a countdown differ by transport.
 */
export const parseRetryAfterSeconds = (rawRetryAfter: unknown): number | undefined => {
  if (typeof rawRetryAfter !== 'string' || rawRetryAfter.length === 0) {
    return undefined
  }
  if (/^\d+$/.test(rawRetryAfter)) {
    return Number(rawRetryAfter)
  }
  const targetMs = Date.parse(rawRetryAfter)
  if (Number.isNaN(targetMs)) {
    return undefined
  }
  return Math.max(0, Math.round((targetMs - Date.now()) / 1000))
}

export const createApiClient = (): AxiosInstance => {
  const apiUrl = normalizeApiUrl(
    import.meta.env.VITE_API_URL || 'http://localhost:8000'
  )

  console.info(`Connecting to API at: ${apiUrl}`)

  const client = axios.create({
    baseURL: apiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: DEFAULT_TIMEOUT_MS,
  })

  // Request interceptor — attaches the Entra access token. The msal import is
  // deferred to avoid a module-eval circular dependency (initMsal calls
  // api.auth.getSession, so msal.ts imports this package). No legacy
  // localStorage token fallback.
  client.interceptors.request.use(
    async (config) => {
      let msal: typeof import('@/services/msal') | null = null
      try {
        msal = await import('@/services/msal')
        const token = await msal.acquireApiToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        // token === null ⟺ no MSAL account (pre-login): proceed without a
        // bearer. Only public discovery routes are reachable then — RequireAuth
        // gates every data view, so a signed-in session always holds an account
        // and never reaches here for a protected call.
        return config
      } catch (err) {
        if (msal && err instanceof InteractionRequiredAuthError) {
          // Signed-in session whose silent token expired. Re-authenticate
          // interactively and ABORT this request: under default-deny (PRD-0044)
          // a protected route must never be hit without a bearer. Proceeding
          // tokenless (the prior behaviour) warn-logged and would 401 at
          // enforce; aborting + redirecting restores the token, then the user
          // retries post-sign-in.
          await msal.redirectForApiToken()
          return Promise.reject(
            new axios.CanceledError('auth redirect in progress')
          )
        }
        // Unexpected (e.g. MSAL not yet initialised) — proceed; the response
        // interceptor's 401 branch performs cold-start sign-in recovery.
        return config
      }
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor — normalises errors and discriminates auth codes.
  client.interceptors.response.use(
    (response) => {
      return response
    },
    async (error: AxiosError<ApiErrorBody>) => {
      if (!error.response) {
        // Nothing answered, so there is no status to report and none is
        // invented: which of the three ways it failed is the whole of what
        // this arm knows, and the reading is made once, here, rather than by
        // each surface sniffing `error.code` or a message for "Network Error".
        return Promise.reject({
          failure: transportFailure(error),
          message: error.message,
        } satisfies ApiError)
      }

      const status = error.response.status
      const errorData = error.response.data as ApiErrorBody | undefined
      // Parse FastAPI's detail once here so every consumer reads a ready-to-
      // display string (and, for a 422, a field-keyed map) — never a JSON blob.
      const { detail, fieldErrors, violations } = normalizeApiErrorDetail(
        errorData?.detail
      )

      // Discriminate auth/availability errors so consumers can show
      // the correct UX without hard-coding numeric codes.
      let authErrorKind: 'unauthenticated' | 'forbidden' | 'unavailable' | undefined
      if (status === 401) {
        authErrorKind = 'unauthenticated'
        // 401 recovery, two cases:
        //  • No active MSAL account (cold deep link / pre-login) → start the
        //    interactive sign-in redirect.
        //  • Active account but the token was rejected (expired, or a stale
        //    cache entry) → force a fresh token and retry the request ONCE;
        //    if renewal needs interaction, fall back to the redirect. Without
        //    this a signed-in session had NO recovery under default-deny
        //    (PRD-0044): every call stayed 401'd until a manual reload.
        const originalRequest = error.config as
          | (InternalAxiosRequestConfig & { _authRetried?: boolean })
          | undefined
        try {
          const msal = await import('@/services/msal')
          if (!msal.msalInstance.getActiveAccount()) {
            await msal.redirectForApiToken()
          } else if (originalRequest && !originalRequest._authRetried) {
            originalRequest._authRetried = true
            try {
              const token = await msal.acquireApiToken({ forceRefresh: true })
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`
                return client(originalRequest)
              }
              // Account present but no token obtainable → re-authenticate.
              await msal.redirectForApiToken()
            } catch {
              // Silent renewal failed (interaction required, etc.).
              await msal.redirectForApiToken()
            }
          }
        } catch {
          // Redirect/import failed (offline, MSAL config error, etc.) — fall
          // through and surface the original 401 to the caller.
        }
      } else if (status === 403) {
        authErrorKind = 'forbidden'
      } else if (status === 503) {
        authErrorKind = 'unavailable'
      }

      const rawRequestId = error.response.headers?.['x-request-id']
      const rawRetryAfter = error.response.headers?.['retry-after']
      const apiError: ApiError = {
        failure: 'response',
        message:
          errorData?.message || errorData?.error || detail || error.message,
        status,
        detail,
        fieldErrors,
        violations,
        domain: extractApiErrorDomain(errorData),
        extra: extractApiErrorExtra(errorData),
        detailBody: extractApiErrorDetailBody(errorData),
        authErrorKind,
        requestId: typeof rawRequestId === 'string' ? rawRequestId : undefined,
        retryAfterSeconds: parseRetryAfterSeconds(rawRetryAfter),
      }
      return Promise.reject(apiError)
    }
  )

  return client
}

/**
 * Singleton client shared by every domain module. Kept internal to the
 * services layer — UI code goes through the `api` facade so requests stay
 * typed; service modules with bespoke needs (githubApi.ts) may import it.
 */
export const apiClient = createApiClient()
